import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, FileTextIcon, HistoryIcon, LayoutGridIcon, ListChecksIcon, Share2Icon } from 'lucide-react';
import { WindowFrame } from '../../ui/WindowFrame';
import { HeroOverview } from './HeroOverview';
import { HeroReview } from './HeroReview';
type Stage = 'overview' | 'review' | 'approved';
const stages: {
  id: Stage;
  label: string;
  duration: number;
}[] = [{
  id: 'overview',
  label: 'Overview',
  duration: 5200
}, {
  id: 'review',
  label: 'Review a finding',
  duration: 4200
}, {
  id: 'approved',
  label: 'Approved',
  duration: 3800
}];
const sidebar = [{
  label: 'Overview',
  icon: LayoutGridIcon,
  count: null,
  stage: 'overview'
}, {
  label: 'Sources',
  icon: FileTextIcon,
  count: '248',
  stage: null
}, {
  label: 'Review queue',
  icon: ListChecksIcon,
  count: '12',
  stage: 'review'
}, {
  label: 'Knowledge map',
  icon: Share2Icon,
  count: null,
  stage: null
}, {
  label: 'Activity',
  icon: HistoryIcon,
  count: null,
  stage: null
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function HeroDashboard() {
  const reduceMotion = useReducedMotion();
  const [stage, setStage] = useState<Stage>('overview');
  const [autoplay, setAutoplay] = useState(true);
  const [hovering, setHovering] = useState(false);
  useEffect(() => {
    if (reduceMotion || !autoplay || hovering) return;
    const index = stages.findIndex(s => s.id === stage);
    const timer = window.setTimeout(() => {
      setStage(stages[(index + 1) % stages.length].id);
    }, stages[index].duration);
    return () => window.clearTimeout(timer);
  }, [stage, autoplay, hovering, reduceMotion]);
  const go = (next: Stage) => {
    setAutoplay(false);
    setStage(next);
  };
  const activeNav = stage === 'overview' ? 'Overview' : 'Review queue';
  return <div className="relative" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} data-id="element-171" data-mp="1ml7zxo3ipbft">
      <div role="tablist" aria-label="Dashboard walkthrough" className="mx-auto mb-5 flex w-fit items-center gap-1 rounded-full glass p-1" data-id="element-172" data-mp="3c090c7ck034i">
        {stages.map(s => {
        const active = s.id === stage;
        return <button key={s.id} role="tab" type="button" aria-selected={active} onClick={() => go(s.id)} className={`relative whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 sm:px-4 ${active ? 'text-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-173" data-mp="32b7jyg23jjq0">
              {active ? <motion.span layoutId="hero-stage" className="absolute inset-0 rounded-full bg-white shadow-sm" transition={{
            duration: 0.25,
            ease
          }} data-id="element-174" data-mp="2jdoa5a7tdx2j" /> : null}
              <span className="relative" data-id="element-175" data-mp="mjthakf5ukip">{s.label}</span>
            </button>;
      })}
      </div>

      <WindowFrame title="Research Workspace" context="Curation & Administration" data-id="element-176" data-mp="270km4ryk89l9">
        <div className="flex" data-id="element-177" data-mp="222rdztdp55ic">
          <nav aria-label="Workspace sample navigation" className="hidden w-48 shrink-0 border-r border-ink/[0.06] p-3 md:block" data-id="element-178" data-mp="223kb02musgna">
            <ul className="space-y-0.5" data-id="element-179" data-mp="2gt8ffcqbzvf8">
              {sidebar.map(item => {
              const active = item.label === activeNav;
              const Icon = item.icon;
              return <li key={item.label} data-id="element-180" data-mp="2ptoi4jz92yj1">
                    <span className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] ${active ? 'bg-white font-medium text-ink shadow-sm' : 'text-ink-soft'}`} data-id="element-181" data-mp="2lr04lyzld3fq">
                      <Icon className={`h-4 w-4 ${active ? 'text-accent' : 'text-ink-faint'}`} aria-hidden="true" data-id="element-182" data-mp="15byyawuodfh3" />
                      <span className="flex-1" data-id="element-183" data-mp="10df9moop5pl1">{item.label}</span>
                      {item.count ? <span className="text-[11px] tabular-nums text-ink-faint" data-id="element-184" data-mp="11u2urdp9hjyy">{item.count}</span> : null}
                    </span>
                  </li>;
            })}
            </ul>
            <div className="mt-6 rounded-xl bg-ink/[0.03] p-3" data-id="element-185" data-mp="1bs8srk7y65yj">
              <p className="text-[11px] font-medium text-ink" data-id="element-186" data-mp="1bt0du13x4lak">Modules</p>
              <p className="mt-1 text-[11px] leading-snug text-ink-muted" data-id="element-187" data-mp="1gqade9l0udc4">Knowledge · Credentials</p>
            </div>
          </nav>

          <div className="min-h-[470px] flex-1 p-4 sm:p-5 md:h-[440px] md:min-h-0" data-id="element-188" data-mp="j1gqq647an3a">
            <AnimatePresence mode="wait" initial={false} data-id="element-189" data-mp="o366fz2dboxp">
              <motion.div key={stage === 'overview' ? 'overview' : 'review'} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.24,
              ease
            }} className="h-full" data-id="element-190" data-mp="t17oqhryzk84">
                {stage === 'overview' ? <HeroOverview onStart={() => go('review')} data-id="element-191" data-mp="2im6cqo4rh11x" /> : <HeroReview approved={stage === 'approved'} onApprove={() => go('approved')} onBack={() => go('overview')} data-id="element-192" data-mp="2ik3l5znymzv3" />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </WindowFrame>

      <AnimatePresence data-id="element-193" data-mp="24llupu6o799h">
        {stage === 'approved' ? <motion.div initial={{
        opacity: 0,
        y: 8,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.24,
        ease,
        delay: 0.15
      }} className="glass absolute -bottom-6 right-4 hidden w-72 items-start gap-3 rounded-2xl p-3.5 shadow-glass sm:flex md:right-10" role="status" data-id="element-194" data-mp="1znkcfbh2jdz2">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ok-soft text-ok-ink" data-id="element-195" data-mp="1gxm2eptpnass">
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-196" data-mp="1bz12kridet4v" />
            </span>
            <div className="text-[12px] leading-snug" data-id="element-197" data-mp="12a44qbekfgfv">
              <p className="font-medium text-ink" data-id="element-198" data-mp="12cqe8gl0o60q">Recorded in activity history</p>
              <p className="mt-0.5 text-ink-muted" data-id="element-199" data-mp="17arwx0ej63bo">Approved by A. Osei · linked to 3 sources · just now</p>
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </div>;
}